import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { container, TYPES } from '../../../shared/services/inversify.config';
import type { IUserProfileService } from '../../../shared/services/interface/IUserProfile';
import type { UserProfile } from '../../../shared/models/userProfile';
import { ServiceConfig } from '../../../shared/services/config/serviceConfig';
import { UserProfileCacheService } from '../../../shared/services/UserProfileCacheService';

interface UserState {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  lastFetchAttempt: number | null;
  consecutiveFailures: number;
}

const initialState: UserState = {
  profile: null,
  isLoading: false,
  error: null,
  lastFetchAttempt: null,
  consecutiveFailures: 0,
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.toLowerCase().endsWith('@rxo.com');
};

export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (params: { forceRefresh?: boolean; email?: string } = {}, { getState, rejectWithValue }) => {
    const { forceRefresh = false, email } = params;
    
    console.log('fetchUserProfile called with params:', { forceRefresh, email });
    
    // Prevent infinite retry on persistent errors (e.g., CORS)
    const state = getState() as { user: UserState };
    const { lastFetchAttempt, consecutiveFailures } = state.user;
    const now = Date.now();
    const MIN_RETRY_INTERVAL = 5000; // 5 seconds minimum between retries
    const MAX_CONSECUTIVE_FAILURES = 3; // Stop after 3 consecutive failures
    
    // Block retry if we had recent failures and haven't waited long enough
    if (!forceRefresh && consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      console.log('Blocked: Too many consecutive failures');
      return rejectWithValue('Too many consecutive failures. Please refresh the page or contact support.');
    }
    
    // Only enforce retry interval if there have been failures (don't block initial fetch)
    if (!forceRefresh && consecutiveFailures > 0 && lastFetchAttempt && (now - lastFetchAttempt) < MIN_RETRY_INTERVAL) {
      console.log('Blocked: Too soon after last attempt');
      return rejectWithValue('Please wait a moment before retrying.');
    }
    
    const userEmail = email || 
                     (typeof window !== 'undefined' ? sessionStorage.getItem('userEmail') : null);
    
    console.log('User email for fetch:', userEmail);
    
    if (!userEmail) {
      console.log('Blocked: No user email');
      return rejectWithValue('No user email available. Please log in again.');
    }
    
    if (!validateEmail(userEmail)) {
      console.log('Blocked: Invalid email format');
      return rejectWithValue('Invalid email format or domain. Email must be from @rxo.com');
    }
    
    if (!forceRefresh) {
      const cachedProfile = UserProfileCacheService.getProfile();
      if (cachedProfile && cachedProfile.email === userEmail) {
        console.log('Returning cached profile:', cachedProfile);
        return cachedProfile;
      }
    }
    
    let profile: UserProfile;
    
    try {
      if (ServiceConfig.USE_MOCK_SERVICES) {
        console.log('Using mock services');
        const mockProfile: UserProfile = {
          id: "mock-user-001",
          firstName: "John",
          lastName: "Doe",
          email: "john.doe@rxo.com",
          isAdmin: true,
          isActive: true,
          roles: [
            { id: 1, roleName: "Admin" },
            { id: 2, roleName: "Devops" },
            { id: 3, roleName: "Onboarding" }
          ]
        };
        
        if (ServiceConfig.MOCK_SETTINGS.SIMULATE_NETWORK_DELAY) {
          const delay = Math.random() * (ServiceConfig.MOCK_SETTINGS.MAX_DELAY - ServiceConfig.MOCK_SETTINGS.MIN_DELAY) + ServiceConfig.MOCK_SETTINGS.MIN_DELAY;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        profile = mockProfile;
      } else {
        console.log('Calling real API - GetUserByEmail with:', userEmail);
        const userService: IUserProfileService = container.get(TYPES.IUserProfileService);
        const fetchedProfile = await userService.GetUserByEmail(userEmail);
        console.log('API response:', fetchedProfile);
        if (!fetchedProfile) {
          return rejectWithValue('User profile not found');
        }
        profile = fetchedProfile;
      }
      
      UserProfileCacheService.setProfile(profile);
      console.log('Profile fetched and cached successfully:', profile);
      return profile;
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      // Detect CORS or network errors
      const errorMessage = error?.message || 'Failed to fetch user profile';
      const isCorsError = errorMessage.toLowerCase().includes('cors') || 
                         errorMessage.toLowerCase().includes('network') ||
                         error?.name === 'NetworkError';
      
      if (isCorsError) {
        return rejectWithValue('Network error: Unable to connect to the server. Please check your connection or contact support.');
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserProfile: (state, action: PayloadAction<UserProfile>) => {
      state.profile = action.payload;
      state.error = null;
    },
    clearUserProfile: (state) => {
      state.profile = null;
      state.error = null;
      state.isLoading = false;
      state.lastFetchAttempt = null;
      state.consecutiveFailures = 0;
      UserProfileCacheService.clearCache();
    },
    setUserError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearUserError: (state) => {
      state.error = null;
      state.consecutiveFailures = 0;
      state.lastFetchAttempt = null;
    },
    invalidateUserCache: () => {
      UserProfileCacheService.invalidateCache();
    },
    resetFailureCount: (state) => {
      state.consecutiveFailures = 0;
      state.lastFetchAttempt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.lastFetchAttempt = Date.now();
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload;
        state.error = null;
        state.consecutiveFailures = 0; // Reset failure count on success
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || action.error.message || 'Failed to load user profile';
        state.consecutiveFailures += 1;
        
        // Keep cached profile if available, to avoid showing "no profile" state
        const cachedProfile = UserProfileCacheService.getProfile();
        if (cachedProfile) {
          state.profile = cachedProfile;
        } else {
          state.profile = null;
        }
      });
  },
});

export const { setUserProfile, clearUserProfile, setUserError, clearUserError, invalidateUserCache, resetFailureCount } = userSlice.actions;
export default userSlice.reducer;
